// Client mirror of app/services/roster_builder.rb.
//
// Greedy slot fill, picks in order: dedicated position slot -> FLEX (RB/WR/TE
// only) -> BENCH, first fit wins and nothing is ever re-shuffled. The server
// uses the same walk for the autopick fit-check and for results, so the two
// must agree exactly or the RosterPanel will show a player in a different slot
// than the results page does. test/roster.test.ts ports the server's
// test/services/roster_builder_test.rb case-for-case to keep them honest.
//
// Deliberate details carried over from the Ruby, not accidents:
//   * a roster_config key that is missing counts as 0, not as unlimited
//   * FLEX eligibility is checked against the *raw* position, before the
//     DEF -> DST mapping (identical for RB/WR/TE, but keeps the walk identical)
//   * a pick that fits nowhere gets no assignment at all rather than being
//     forced into BENCH

import { ROSTER_SLOTS } from '../types/protocol'
import type { RosterConfig, RosterSlot } from '../types/protocol'

export const FLEX_ELIGIBLE = ['RB', 'WR', 'TE'] as const

// The players table stores Sleeper's "DEF"; roster_config uses "DST".
const POSITION_TO_SLOT: Record<string, string> = { DEF: 'DST' }

/** Roster-config key for a raw player position. */
export function slotKeyFor(position: string): string {
  return POSITION_TO_SLOT[position] ?? position
}

/** The minimum a pick has to look like to be slotted. */
export interface RosterPick {
  /** Stable identity — the pick's overall number in this app. */
  id: number
  player_id: number | null
  position: string | null
}

type Counts = Record<string, number>

function countsFrom(config: RosterConfig): Counts {
  return { ...config } as Counts
}

function isFlexEligible(position: string): boolean {
  return (FLEX_ELIGIBLE as readonly string[]).includes(position)
}

/**
 * Slots left after walking the picks. Mirrors RosterBuilder#remaining_slots.
 */
export function remainingSlots(config: RosterConfig, picks: RosterPick[]): Counts {
  const counts = countsFrom(config)

  for (const pick of picks) {
    if (pick.player_id == null) continue
    const position = pick.position
    if (!position) continue

    const key = slotKeyFor(position)

    if ((counts[key] ?? 0) > 0) counts[key] -= 1
    else if (isFlexEligible(position) && (counts.FLEX ?? 0) > 0) counts.FLEX -= 1
    else if ((counts.BENCH ?? 0) > 0) counts.BENCH -= 1
  }

  return counts
}

/**
 * Can one more player of this position be rostered? Mirrors RosterBuilder.fits?.
 */
export function fitsRoster(
  config: RosterConfig,
  picks: RosterPick[],
  position: string,
): boolean {
  const remaining = remainingSlots(config, picks)
  const key = slotKeyFor(position)

  if ((remaining[key] ?? 0) > 0) return true
  if (isFlexEligible(position) && (remaining.FLEX ?? 0) > 0) return true
  if ((remaining.BENCH ?? 0) > 0) return true

  return false
}

/**
 * pick id -> assigned slot. Mirrors RosterBuilder.build; picks that fit
 * nowhere are simply absent from the map.
 */
export function buildRoster(config: RosterConfig, picks: RosterPick[]): Map<number, string> {
  const assignments = new Map<number, string>()
  const remaining = countsFrom(config)

  for (const pick of picks) {
    if (pick.player_id == null) continue
    const position = pick.position
    if (!position) continue

    const key = slotKeyFor(position)
    let assigned: string | null = null

    if ((remaining[key] ?? 0) > 0) {
      remaining[key] -= 1
      assigned = key
    } else if (isFlexEligible(position) && (remaining.FLEX ?? 0) > 0) {
      remaining.FLEX -= 1
      assigned = 'FLEX'
    } else if ((remaining.BENCH ?? 0) > 0) {
      remaining.BENCH -= 1
      assigned = 'BENCH'
    }

    if (assigned) assignments.set(pick.id, assigned)
  }

  return assignments
}

/** One line of the roster panel: a slot, and the pick filling it (or nothing). */
export interface RosterRow<T extends RosterPick = RosterPick> {
  slot: RosterSlot
  /** True when the slot exists in roster_config; false for overflow rows. */
  configured: boolean
  pick: T | null
}

/**
 * The display form: every configured slot in ROSTER_SLOTS order, filled or
 * empty, so an owner sees the shape of their roster and not just what they own.
 *
 * Anything that fit nowhere (a commissioner force-pick past the config, or a
 * roster_config edited down mid-draft) is appended as an unconfigured overflow
 * row — dropping those players off the panel silently would be worse than
 * showing a roster that is one line too long.
 */
export function buildRosterRows<T extends RosterPick>(
  config: RosterConfig,
  picks: T[],
): Array<RosterRow<T>> {
  const assignments = buildRoster(config, picks)

  const bySlot = new Map<string, T[]>()
  const overflow: T[] = []

  for (const pick of picks) {
    if (pick.player_id == null || !pick.position) continue
    const slot = assignments.get(pick.id)
    if (!slot) {
      overflow.push(pick)
      continue
    }
    const bucket = bySlot.get(slot)
    if (bucket) bucket.push(pick)
    else bySlot.set(slot, [pick])
  }

  const rows: Array<RosterRow<T>> = []

  for (const slot of ROSTER_SLOTS) {
    const count = config[slot] ?? 0
    const filled = bySlot.get(slot) ?? []
    for (let i = 0; i < count; i++) {
      rows.push({ slot, configured: true, pick: filled[i] ?? null })
    }
  }

  for (const pick of overflow) {
    rows.push({ slot: 'BENCH', configured: false, pick })
  }

  return rows
}
